import { Link } from "react-router-dom";
import CountUp from "react-countup";
import Marquee from "react-fast-marquee";
import { ArrowRight, Quote, Star } from "lucide-react";
import { STATS, MARQUEE_ITEMS, SERVICES } from "../data/content";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, Overline } from "../components/Reveal";
import { BrandText } from "../components/Brand";
import Timeline from "../components/Timeline";
import CTASection from "../components/CTASection";

export default function Home() {
  const { lang, t } = useLang();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden grain">
        <div
          className="absolute inset-0 opacity-[0.26]"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1699621106755-4fe40ce95d64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBvZmZpY2UlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODA0NDY5NTZ8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/85 to-navy" />
        <div className="absolute inset-0 gold-glow" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-32 pb-20 w-full">
          <div className="max-w-3xl">
            <Reveal>
              <Overline>{t.hero.overline}</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] mt-6">
                {t.hero.titleA}{" "}
                <span className="text-gold-gradient">{t.hero.accent}</span>
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-7 text-lg text-muted2 max-w-xl leading-relaxed">
                {t.hero.body}
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-consultation"
                  className="bg-gold text-deepblack px-9 py-4 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-goldhover transition-colors inline-flex items-center justify-center gap-2"
                >
                  {t.hero.ctaConsult} <ArrowRight size={15} />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-services"
                  className="border border-gold/50 text-gold px-9 py-4 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-gold/10 transition-colors inline-flex items-center justify-center"
                >
                  {t.hero.ctaServices}
                </Link>
              </div>
            </Reveal>
          </div>

          {/* STATS */}
          <Reveal delay={0.4}>
            <div className="mt-20 liquid-glass-light rounded-2xl grid grid-cols-1 sm:grid-cols-3 max-w-4xl overflow-hidden">
              {STATS.map((s, i) => (
                <div
                  key={i}
                  data-testid={`hero-stat-${i}`}
                  className={`px-8 py-9 text-center ${
                    i > 0 ? "sm:border-l border-white/10" : ""
                  }`}
                >
                  <div className="font-serif text-5xl sm:text-6xl text-gold-gradient font-bold">
                    <CountUp end={s.value} duration={2.2} enableScrollSpy scrollSpyOnce />
                    <span className="text-3xl sm:text-4xl">{s.suffix[lang]}</span>
                  </div>
                  <p className="mt-3 text-xs uppercase tracking-[0.2em] text-muted2">
                    {s.label[lang]}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-gold/15 bg-deepblack py-5">
        <Marquee speed={40} gradient={false} autoFill>
          {MARQUEE_ITEMS[lang].map((item) => (
            <span
              key={item}
              className="mx-8 uppercase tracking-[0.25em] text-sm text-gold/80 font-medium inline-flex items-center gap-8"
            >
              {item}
              <span className="text-gold/40">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* SERVICES OVERVIEW */}
      <section className="py-24 sm:py-32 bg-navy">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="text-center mb-16">
            <Overline>{t.homeServices.overline}</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4">
              {t.homeServices.titleA}
              <span className="text-gold-gradient">{t.homeServices.accent}</span>
              {t.homeServices.titleB}
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title.en} delay={0.05 * i}>
                  <div
                    data-testid={`home-service-card-${i}`}
                    className="group h-full liquid-glass-light rounded-2xl p-8 hover:border-gold/50 transition-colors duration-300"
                  >
                    <div className="h-12 w-12 flex items-center justify-center border border-gold/30 text-gold mb-6 group-hover:bg-gold group-hover:text-deepblack transition-colors duration-300">
                      <Icon size={22} />
                    </div>
                    <h3 className="font-serif text-2xl text-offwhite mb-3">
                      {s.title[lang]}
                    </h3>
                    <p className="text-sm text-muted2 leading-relaxed">
                      {s.short[lang]}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="text-center mt-14">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-gold text-sm font-semibold uppercase tracking-[0.15em] hover:gap-3 transition-all"
            >
              {t.homeServices.viewAll} <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS + TESTIMONIAL + CTA — one continuous backdrop, no visible cuts */}
      <div className="relative overflow-hidden grain">
        {/* continuous navy -> deep-black flow (blends with the services section above) */}
        <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy to-deepblack" />

        {/* HOW IT WORKS */}
        <Timeline seamless />

        {/* TESTIMONIAL + FINAL CTA share a single soft gold wash */}
        <div className="relative">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(130% 60% at 50% 32%, rgba(201,168,76,0.14), rgba(201,168,76,0.04) 48%, transparent 76%)",
            }}
          />

          {/* TESTIMONIAL */}
          <section className="relative py-24 sm:py-32">
            <div className="relative max-w-4xl mx-auto px-6 sm:px-8 text-center">
              <Reveal>
                <Quote size={48} className="text-gold/40 mx-auto mb-8" />
                <div className="flex justify-center gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} className="text-gold fill-gold" />
                  ))}
                </div>
                <blockquote className="font-serif text-3xl sm:text-4xl leading-snug text-offwhite">
                  «<BrandText>{t.testimonial.quote}</BrandText>»
                </blockquote>
                <p className="mt-8 text-sm uppercase tracking-[0.2em] text-gold">
                  {t.testimonial.author}
                </p>
              </Reveal>
            </div>
          </section>

          {/* FINAL CTA — seamless so it shares the backdrop */}
          <CTASection seamless />
        </div>
      </div>
    </div>
  );
}
